import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Popover, IconButton } from "@material-ui/core";
import { CloseXIcon } from "../ChatiaIcons";
import { Pencil } from "lucide-react";
import useActionBtnStyles from "../../styles/actionBtnStyles";
import { i18n } from "../../translate/i18n";

const WEEKDAYS_LONG = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
const MONTHS_LONG = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

const getReminderLabels = () => ({
  0: i18n.t("eventDetailPopover.reminders.none"),
  5: i18n.t("eventDetailPopover.reminders.min5"),
  10: i18n.t("eventDetailPopover.reminders.min10"),
  15: i18n.t("eventDetailPopover.reminders.min15"),
  30: i18n.t("eventDetailPopover.reminders.min30"),
  60: i18n.t("eventDetailPopover.reminders.hour1"),
  120: i18n.t("eventDetailPopover.reminders.hour2"),
  1440: i18n.t("eventDetailPopover.reminders.day1"),
});

const getEventTypeLabels = () => ({
  reuniao: i18n.t("eventDetailPopover.eventTypes.meeting"),
  tarefa: i18n.t("eventDetailPopover.eventTypes.task"),
});
const EVENT_TYPE_COLORS = {
  reuniao: { bg: "rgba(59, 130, 246, 0.12)", color: "#2563eb" },
  tarefa: { bg: "rgba(34, 197, 94, 0.12)", color: "#16a34a" },
};

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 2V6M8 2V6" />
    <path d="M13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22H13C16.7712 22 18.6569 22 19.8284 20.8284C21 19.6569 21 17.7712 21 14V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4Z" />
    <path d="M3 10H21" />
    <path d="M15.5 15.5V17.5M17 16.5C17 17.3284 16.3284 18 15.5 18C14.6716 18 14 17.3284 14 16.5C14 15.6716 14.6716 15 15.5 15C16.3284 15 17 15.6716 17 16.5Z" />
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" />
    <path d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z" />
  </svg>
);

const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15.5 18C15.5 19.933 13.933 21.5 12 21.5C10.067 21.5 8.5 19.933 8.5 18" />
    <path d="M19.2311 18H4.76887C3.79195 18 3 17.208 3 16.2311C3 15.762 3.18636 15.3121 3.51809 14.9803L4.12132 14.3771C4.68393 13.8145 5 13.0514 5 12.2558V9.5C5 5.63401 8.13401 2.5 12 2.5C15.866 2.5 19 5.634 19 9.5V12.2558C19 13.0514 19.3161 13.8145 19.8787 14.3771L20.4819 14.9803C20.8136 15.3121 21 15.762 21 16.2311C21 17.208 20.208 18 19.2311 18Z" />
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 8.5C14 5.73858 11.7614 3.5 9 3.5C6.23858 3.5 4 5.73858 4 8.5C4 11.2614 6.23858 13.5 9 13.5C11.7614 13.5 14 11.2614 14 8.5Z" />
    <path d="M16 20.5C16 16.634 12.866 13.5 9 13.5C5.13401 13.5 2 16.634 2 20.5" />
    <path d="M16 12.3929C16 12.3929 17.2 13.0447 17.8 14C17.8 14 19.6 10.25 22 9" />
  </svg>
);

const TrashSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5" />
    <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" />
    <path d="M9.5 16.5L9.5 10.5" />
    <path d="M14.5 16.5L14.5 10.5" />
  </svg>
);

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const textPrimary = isDark ? "#e9edef" : "#111b21";
  const textSecondary = isDark ? "#8696a0" : "#5b6470";
  const textMuted = isDark ? "#566069" : "#94a3b8";
  return ({
  paper: {
    borderRadius: "21px 21px 12px 12px",
    width: 360,
    maxWidth: "95vw",
    overflow: "hidden",
    boxShadow: isDark
      ? "0 8px 24px rgba(0,0,0,0.45)"
      : "0 4px 20px rgba(15,23,42,0.10)",
    border: isDark
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(0,0,0,0.06)",
    background: isDark ? "#121212" : "#ffffff",
  },
  header: {
    ...theme.chatiaDS.header,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    ...theme.chatiaDS.headerTitle,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  headerIconBtn: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    color: "#ffffff",
    transition: "all 0.2s ease",
    padding: 6,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.22)",
      color: "#ffffff",
    },
  },
  content: {
    padding: "16px 18px",
    background: isDark ? "#121212" : "#ffffff",
  },
  badge: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 10px",
    borderRadius: 99,
    marginBottom: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: textPrimary,
    marginBottom: 4,
    lineHeight: 1.3,
  },
  description: {
    fontSize: 13,
    color: textSecondary,
    marginBottom: 16,
    lineHeight: 1.4,
  },
  detailRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "6px 0",
    color: textPrimary,
  },
  detailIcon: {
    color: textSecondary,
    flexShrink: 0,
    marginTop: 1,
  },
  detailText: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
    color: textPrimary,
  },
  detailSub: {
    fontSize: 12,
    color: textSecondary,
    fontWeight: 400,
  },
  divider: {
    height: 1,
    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    margin: "12px 0",
  },
  createdBy: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: textMuted,
    padding: "4px 0",
  },
  });
});

const EventDetailPopover = ({ open, anchorEl, side = "right", event, onClose, onEdit, onDelete }) => {
  const classes = useStyles();

  if (!event) return null;

  const typeStyle = EVENT_TYPE_COLORS[event.eventType] || EVENT_TYPE_COLORS.reuniao;

  // Format date
  const formatFullDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const weekday = WEEKDAYS_LONG[dt.getDay()];
    const month = MONTHS_LONG[dt.getMonth()];
    return `${weekday}, ${String(d).padStart(2, "0")} de ${month} de ${y}`;
  };

  const formatTimeRange = () => {
    if (event.allDay) return i18n.t("eventDetailPopover.allDay");
    const start = event.startTime || "09:00";
    if (!event.duration) return start;
    const [h, m] = start.split(":").map(Number);
    const totalMin = h * 60 + m + event.duration;
    const endH = String(Math.floor(totalMin / 60) % 24).padStart(2, "0");
    const endM = String(totalMin % 60).padStart(2, "0");
    return `${start} - ${endH}:${endM}`;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: side === "left" ? "left" : "right" }}
      transformOrigin={{ vertical: "top", horizontal: side === "left" ? "right" : "left" }}
      PaperProps={{ className: classes.paper }}
    >
      {/* Header */}
      <div className={`${classes.header} chatia-ds-header`}>
        <div className={classes.headerTitle}>
          {i18n.t("eventDetailPopover.title")}
        </div>
        <div className={classes.headerActions}>
          <IconButton
            size="small"
            className={classes.headerIconBtn}
            onClick={() => { onEdit(event); onClose(); }}
            title={i18n.t("eventDetailPopover.edit")}
          >
            <Pencil size={16} strokeWidth={1.8} />
          </IconButton>
          <IconButton
            size="small"
            className={classes.headerIconBtn}
            onClick={() => { onDelete(event); onClose(); }}
            title={i18n.t("eventDetailPopover.delete")}
          >
            <TrashSvg />
          </IconButton>
          <IconButton size="small" onClick={onClose} className={classes.headerIconBtn}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      <div className={classes.content}>
        {/* Badge tipo */}
        <span className={classes.badge} style={{ background: typeStyle.bg, color: typeStyle.color }}>
          {getEventTypeLabels()[event.eventType] || event.eventType}
        </span>

        {/* Título */}
        <div className={classes.title}>{event.title}</div>

        {/* Descrição */}
        {event.description && (
          <div className={classes.description}>{event.description}</div>
        )}

        <div className={classes.divider} />

        {/* Data */}
        <div className={classes.detailRow}>
          <span className={classes.detailIcon}><CalendarIcon /></span>
          <div>
            <div className={classes.detailText}>{formatFullDate(event.date)}</div>
            <div className={classes.detailSub}>{formatTimeRange()}</div>
          </div>
        </div>

        {/* Local */}
        {event.location && (
          <div className={classes.detailRow}>
            <span className={classes.detailIcon}><LocationIcon /></span>
            <div className={classes.detailText}>{event.location}</div>
          </div>
        )}

        {/* Lembrete */}
        {event.reminder > 0 && (
          <div className={classes.detailRow}>
            <span className={classes.detailIcon}><BellIcon /></span>
            <div className={classes.detailText}>{getReminderLabels()[event.reminder] || i18n.t("eventDetailPopover.reminders.minBefore", { min: event.reminder })}</div>
          </div>
        )}

        <div className={classes.divider} />

        {/* Criado por */}
        {event.user && (
          <div className={classes.createdBy}>
            <UserIcon />
            {i18n.t("eventDetailPopover.createdBy")}: {event.user.name}
          </div>
        )}
      </div>
    </Popover>
  );
};

export default EventDetailPopover;
